import { Link } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/feature/SEOHead";
import SchemaOrg from "@/components/feature/SchemaOrg";
import { movies, categories } from "@/mocks/movies";
import type { Movie } from "@/mocks/movies";

const heroMovies = movies.filter((m) => m.isHot).slice(0, 4);

function MovieCard({ movie }: { movie: Movie }) {
  const categoryName = categories.find((c) => c.slug === movie.category)?.name || movie.category;
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-accent-300 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden">
        <img
          src={movie.posterUrl}
          alt={movie.title}
          title={`${movie.title} - 屁屁影院在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
        <div className="absolute top-3 left-3 px-2 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-md">
          {categoryName}
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 text-amber-400 text-xs font-semibold rounded-md">
          <i className="ri-star-fill text-[10px]"></i>
          {movie.rating}
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <h3 className="text-base font-semibold text-white truncate font-heading">
            {movie.title}
          </h3>
          <p className="text-xs text-white/70 mt-1 truncate">
            {movie.actors.slice(0, 2).join(" / ")}
          </p>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const [heroIndex, setHeroIndex] = useState(0);

  const nextHero = useCallback(() => {
    setHeroIndex((prev) => (prev + 1) % heroMovies.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextHero, 5000);
    return () => clearInterval(timer);
  }, [nextHero]);

  const hotMovies = movies.filter((m) => m.isHot);
  const newMovies = movies.filter((m) => m.isNew);
  const moviesByCategory = categories.map((cat) => ({
    ...cat,
    items: movies.filter((m) => m.category === cat.slug).slice(0, 4),
  }));

  const canonicalUrl = "https://www.pipicinema.com/";

  return (
    <>
      <SEOHead
        title="屁屁影院 - 海量高清影视资源免费在线观看"
        description="屁屁影院是一个提供海量高清影视资源的在线观看平台，涵盖电影、电视剧、综艺、动漫等多种类型，无需注册即可免费畅享最新最热门的影视内容，支持多终端流畅播放，是您追剧看片的首选平台。"
        keywords="屁屁影院,屁屁影院在线观看,屁屁影院最新电影,屁屁影院电视剧,屁屁影院免费高清,屁屁影院官网入口,在线影院免费看片"
        canonicalUrl={canonicalUrl}
        ogType="website"
      />
      <SchemaOrg
        type="Organization"
        data={{
          name: "屁屁影院",
          url: "https://www.pipicinema.com",
          description: "海量高清影视资源免费在线观看平台",
        }}
      />
      <SchemaOrg
        type="WebSite"
        data={{
          name: "屁屁影院",
          url: "https://www.pipicinema.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://www.pipicinema.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[500px] md:h-[650px] overflow-hidden">
          {heroMovies.map((movie, index) => (
            <div
              key={movie.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === heroIndex ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={movie.heroUrl}
                alt={movie.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/60"></div>
            </div>
          ))}
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 w-full">
              <div className="max-w-xl">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-accent-500/90 text-background-50 text-sm font-medium rounded-full mb-4">
                  <i className="ri-fire-fill"></i>
                  <span>热播推荐</span>
                </div>
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 font-heading leading-tight">
                  {heroMovies[heroIndex].title}
                </h1>
                <p className="text-white/80 text-sm md:text-base leading-relaxed mb-6 line-clamp-2">
                  {heroMovies[heroIndex].description}
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1 text-amber-400 text-sm font-semibold">
                    <i className="ri-star-fill"></i>
                    {heroMovies[heroIndex].rating}
                  </span>
                  <span className="text-white/60 text-sm">
                    {heroMovies[heroIndex].year}
                  </span>
                  <span className="text-white/60 text-sm">
                    {heroMovies[heroIndex].genre.join(" / ")}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <Link
                    to={`/article/${heroMovies[heroIndex].id}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即观看
                  </Link>
                  <Link
                    to={`/category/${heroMovies[heroIndex].category}`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 text-white backdrop-blur-sm font-medium rounded-lg hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-list-check-2"></i>
                    更多推荐
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {heroMovies.map((_, index) => (
              <button
                key={index}
                onClick={() => setHeroIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  index === heroIndex
                    ? "bg-accent-500 w-7"
                    : "bg-white/40 hover:bg-white/70"
                }`}
              ></button>
            ))}
          </div>
        </section>

        {/* Category Quick Nav */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 -mt-10 relative z-10 mb-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center gap-3 p-5 bg-background-50 rounded-xl border border-background-200 hover:border-accent-300 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <span className="text-sm font-semibold text-foreground-800 font-heading">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Hot Movies */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 mb-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              <i className="ri-fire-fill text-accent-500 mr-2"></i>
              热播推荐
            </h2>
            <Link
              to="/category/dianying"
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 cursor-pointer"
            >
              查看更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {hotMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        {/* New Movies */}
        <section className="max-w-[1400px] mx-auto px-4 md:px-6 mb-14">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              <i className="ri-sparkling-2-line text-accent-500 mr-2"></i>
              最新上线
            </h2>
            <Link
              to="/category/dianying"
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 cursor-pointer"
            >
              查看更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {newMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        {/* Category Sections */}
        {moviesByCategory.map((cat) => (
          <section
            key={cat.slug}
            className="max-w-[1400px] mx-auto px-4 md:px-6 mb-14"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
                <i className={`${cat.icon} text-accent-500 mr-2`}></i>
                {cat.name}
              </h2>
              <Link
                to={`/category/${cat.slug}`}
                className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 cursor-pointer"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {cat.items.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </section>
        ))}

        {/* Features */}
        <section className="bg-background-100 py-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10 font-heading">
              为什么选择屁屁影院
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: "ri-film-line",
                  title: "海量资源",
                  desc: "涵盖电影、电视剧、综艺、动漫等数万部影视作品，每日更新",
                },
                {
                  icon: "ri-hd-line",
                  title: "高清画质",
                  desc: "支持1080P超清画质播放，带来极致视觉享受",
                },
                {
                  icon: "ri-device-line",
                  title: "多端支持",
                  desc: "电脑、手机、平板全终端适配，随时随地畅享观影",
                },
                {
                  icon: "ri-shield-check-line",
                  title: "无需注册",
                  desc: "无需登录即可免费观看所有内容，零门槛畅享影视",
                },
              ].map((feature, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center text-center p-6"
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-50 text-accent-600 mb-4">
                    <i className={`${feature.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2 font-heading">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}